import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Button, Form, Row, Col } from 'react-bootstrap';
import { Trans, useTranslation } from 'react-i18next';
import axiosInstance from 'axiosInstance';

const RegistrationForm = ({ hasLabel }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // State
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    isAccepted: false
  });
  const [loading, setLoading] = useState(false);

  // Handler
  const handleSubmit = async e => {
    e.preventDefault();
    if (formData.password !== formData.confirmPassword) {
      toast.error(t('LOGIN.PASSWORDS_MUST_MATCH', { defaultValue: 'Passwords must match' }), {
        theme: 'colored'
      });
      return;
    }

    setLoading(true);
    try {
      const salesLinkToken = String(searchParams.get('sales_link') || '').trim();
      const refCode = String(searchParams.get('ref') || '').trim();
      const normalizedName = formData.name.trim();
      const emailLocalPart = String(formData.email || '').split('@')[0] || 'user';
      await axiosInstance.post('login/register', {
        username: `${emailLocalPart}-${Date.now()}`,
        email: formData.email.trim(),
        password: formData.password,
        company_name: normalizedName,
        sales_link_token: salesLinkToken || undefined,
        ref_code: refCode || undefined,
      });
      toast.success(t('LOGIN.SUCCESSFUL_REGISTER', { name: formData.name }), {
        theme: 'colored'
      });
      navigate('/login');
    } catch (error) {
      toast.error(error.response?.data?.error || t('LOGIN.REGISTER_ERROR', { defaultValue: 'Unable to register' }), {
        theme: 'colored'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleFieldChange = e => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <Form onSubmit={handleSubmit}>
      <Form.Group className="mb-3">
        {hasLabel && <Form.Label>{t('LOGIN.NAME')}</Form.Label>}
        <Form.Control
          placeholder={!hasLabel ? t('LOGIN.NAME') : ''}
          value={formData.name}
          name="name"
          onChange={handleFieldChange}
          type="text"
          disabled={loading}
        />
      </Form.Group>

      <Form.Group className="mb-3">
        {hasLabel && <Form.Label>{t('LOGIN.EMAIL')}</Form.Label>}
        <Form.Control
          placeholder={!hasLabel ? t('LOGIN.EMAIL') : ''}
          value={formData.email}
          name="email"
          onChange={handleFieldChange}
          type="text"
          disabled={loading}
        />
      </Form.Group>

      <Row className="g-2 mb-3">
        <Form.Group as={Col} sm={6}>
          {hasLabel && <Form.Label>{t('LOGIN.PASSWORD')}</Form.Label>}
          <Form.Control
            placeholder={!hasLabel ? t('LOGIN.PASSWORD') : ''}
            value={formData.password}
            name="password"
            onChange={handleFieldChange}
            type="password"
            disabled={loading}
          />
        </Form.Group>
        <Form.Group as={Col} sm={6}>
          {hasLabel && <Form.Label>{t('LOGIN.CONFIRM_PASSWORD')}</Form.Label>}
          <Form.Control
            placeholder={!hasLabel ? t('LOGIN.CONFIRM_PASSWORD') : ''}
            value={formData.confirmPassword}
            name="confirmPassword"
            onChange={handleFieldChange}
            type="password"
            disabled={loading}
          />
        </Form.Group>
      </Row>

      <Form.Group className="mb-3">
        <Form.Check type="checkbox" id="acceptCheckbox" className="form-check">
          <Form.Check.Input
            type="checkbox"
            name="isAccepted"
            checked={formData.isAccepted}
            onChange={e =>
              setFormData({
                ...formData,
                isAccepted: e.target.checked
              })
            }
            disabled={loading}
          />
          <Form.Check.Label className="form-label">
            <Trans i18nKey="LOGIN.ACCEPT_TERMS">
              <Link to="/terms"></Link>
              <Link to="/privacy-policy"></Link>
            </Trans>
          </Form.Check.Label>
        </Form.Check>
      </Form.Group>

      <Form.Group className="mb-4">
        <Button
          className="w-100"
          type="submit"
          disabled={
            !formData.name ||
            !formData.email ||
            !formData.password ||
            !formData.confirmPassword ||
            !formData.isAccepted ||
            loading
          }
        >
          {loading ? t('LOGIN.LOADING', { defaultValue: 'Loading...' }) : t('LOGIN.REGISTER')}
        </Button>
      </Form.Group>
    </Form>
  );
};

RegistrationForm.propTypes = {
  hasLabel: PropTypes.bool
};

export default RegistrationForm;
